import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import type { Product, WidgetAuth } from '../types/api';

interface UseProductsReturn {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProducts(
  auth: WidgetAuth | null,
  limit: number = 10,
  callId?: number
): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!auth) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Pass callId through so the backend can filter products to the attached Look
      const response = await apiClient.getProducts(auth, limit, callId);
      setProducts(response.products);
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to load products';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [auth, limit, callId]);

  useEffect(() => {
    if (auth) {
      fetchProducts();
    }
  }, [auth, fetchProducts]);

  return {
    products,
    isLoading,
    error,
    refetch: fetchProducts,
  };
}
